/**
 * Moving a whole workspace between machines.
 *
 * > *"I made an account on my pc and mac, with different mails. On my mac I logged out and logged
 * > in with my pc mail, expecting to get into the pc account with all the saved solutions etc, and
 * > yet that did not happen."*
 *
 * It could not have. **Work has never been on a server.** Every sheet, course, chapter, task and
 * log entry lives in IndexedDB in the browser on the machine it was made on, which is the whole
 * reason this app opens instantly and works with no network. An account buys exactly one thing —
 * a join code that resolves on somebody else's machine — and it has never been an address that
 * work hangs off. Signing in as another address is therefore not a way to reach another device's
 * sheets, and no change to signing in could make it one.
 *
 * What was missing is the thing that actually moves work: a file. This writes every store of the
 * workspace out as one JSON document and reads one back in, so the PC's evening of solutions
 * arrives on the Mac exactly as it was.
 *
 * ── The rule that makes it safe to press ───────────────────────────────────────────────────
 *
 * Importing **merges** and never wipes. A record whose id is already here is kept unless the one
 * arriving is newer, and anything the file does not mention is left alone. So importing the same
 * file twice changes nothing the second time, and importing the Mac's file onto the PC cannot
 * cost the PC a sheet it has and the file does not. Losing work to a restore is the one failure
 * this feature may not have.
 */
import { DB_VERSION, STORES, openDb } from './storage';

/** Bumped only if the SHAPE changes. The app's build id travels beside it for diagnosis. */
export const TRANSFER_VERSION = 1;

export interface Transfer {
  kind: 'paint6000.workspace';
  version: number;
  /** The database version the file was written from, so an older shape can be recognised. */
  db: number;
  at: string;
  build: string;
  counts: Record<string, number>;
  stores: Record<string, unknown[]>;
}

const ALL = Object.values(STORES);

/** Everything, as one document. */
export async function exportWorkspace(build = 'dev'): Promise<Transfer> {
  const db = await openDb();
  const stores: Record<string, unknown[]> = {};
  const counts: Record<string, number> = {};

  for (const name of ALL) {
    if (!db.objectStoreNames.contains(name)) continue;
    const rows = await new Promise<unknown[]>((resolve, reject) => {
      const request = db.transaction(name, 'readonly').objectStore(name).getAll();
      request.onsuccess = () => resolve(request.result as unknown[]);
      request.onerror = () => reject(request.error);
    });
    stores[name] = rows;
    counts[name] = rows.length;
  }

  return {
    kind: 'paint6000.workspace',
    version: TRANSFER_VERSION,
    db: DB_VERSION,
    at: new Date().toISOString(),
    build,
    counts,
    stores,
  };
}

export interface ImportReport {
  added: Record<string, number>;
  updated: Record<string, number>;
  kept: Record<string, number>;
  total: number;
}

/** A row's own timestamp, for deciding which of two copies of one id is the later. */
export const stampOf = (row: unknown): number => {
  if (!row || typeof row !== 'object') return 0;
  const record = row as Record<string, unknown>;
  const at = record.updatedAt ?? record.at ?? record.createdAt;
  return typeof at === 'number' ? at : 0;
};

export const idOf = (row: unknown): string | null => {
  if (!row || typeof row !== 'object') return null;
  const id = (row as Record<string, unknown>).id;
  return typeof id === 'string' ? id : null;
};

export function isTransfer(value: unknown): value is Transfer {
  if (!value || typeof value !== 'object') return false;
  const file = value as Partial<Transfer>;
  return file.kind === 'paint6000.workspace'
    && typeof file.version === 'number'
    && !!file.stores && typeof file.stores === 'object';
}

/**
 * Merge a file into this device.
 *
 * Newer wins, per record, by the record's own timestamp — never by which machine is running the
 * import. A record with no timestamp at all is only ever ADDED, never used to overwrite one that
 * is here: with nothing to compare, keeping what the device has is the answer that cannot lose
 * anything.
 */
export async function importWorkspace(file: Transfer): Promise<ImportReport> {
  const db = await openDb();
  const report: ImportReport = { added: {}, updated: {}, kept: {}, total: 0 };

  for (const name of ALL) {
    const rows = file.stores[name];
    if (!Array.isArray(rows) || !db.objectStoreNames.contains(name)) continue;

    let added = 0;
    let updated = 0;
    let kept = 0;

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(name, 'readwrite');
      const store = tx.objectStore(name);
      for (const row of rows) {
        const id = idOf(row);
        if (!id) continue;
        const existing = store.get(id);
        existing.onsuccess = () => {
          const here = existing.result as unknown;
          if (here === undefined) { store.put(row); added += 1; return; }
          const mine = stampOf(here);
          const theirs = stampOf(row);
          if (theirs > mine) { store.put(row); updated += 1; } else { kept += 1; }
        };
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });

    report.added[name] = added;
    report.updated[name] = updated;
    report.kept[name] = kept;
    report.total += added + updated;
  }

  return report;
}

/** The filename a transfer is offered under. Dated, because there will be more than one. */
export const transferFilename = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `paint6000-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.json`;
};
